import React, { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import {
  ImageResponse,
  PlanResponse,
  PublicationResponse,
} from '../../../apis/fvillage'
import { PATH } from '../../../constants'
import {
  useCreateNextPath,
  useImages,
  useMeal,
  usePropertyProvider,
  usePublication,
  useSearchQueryParams,
} from '../../../hooks'
import {
  BaseText,
  ButtonWrapper as Button,
  Col,
  Container,
  Icon,
  Modal,
  Row,
  SlideShow,
} from '../../basics'
import { CancelPolicy, TaxLabel } from '../../contents'
import { GalleryModal, PlanDetailModal } from '../../modals'
import * as styles from '../../rooms/RoomItem/RoomItem.module.scss'

type HotelPlanItemProps = PlanResponse

// Reuse PlanItem's copy namespace for the shared fields (check-in/out, meal,
// cancel, price, detail); HotelPlanItem-specific copy lives under its own key.
const translateNamespace = 'components.PlanItem'
const hotelPlanNamespace = 'components.HotelPlanItem'

type UseDefaultValueProps = {
  publications: PublicationResponse[] | undefined
  images: ImageResponse[] | undefined
  mealType: number | null
}

const useDefaultValues = ({
  publications,
  images,
  mealType,
}: UseDefaultValueProps) => {
  const { i18n } = useTranslation()
  const publication = usePublication(i18n.language, publications)
  const itemImages = useImages(images)
  const { meal, withMeal } = useMeal(mealType)

  return useMemo(
    () => ({
      planName: publication?.name ?? '',
      planDescription: publication?.description ?? '',
      planDescriptionDetail: publication?.descriptionDetail ?? '',
      itemImages:
        itemImages.length > 0
          ? itemImages
          : [
              {
                pc: { src: '/img/hotel01.jpg' },
                sp: { src: '/img/hotel01.jpg' },
                alt: publication?.name ?? '',
              },
              {
                pc: { src: '/img/hotel02.jpg' },
                sp: { src: '/img/hotel02.jpg' },
                alt: publication?.name ?? '',
              },
            ],
      meal,
      withMeal,
    }),
    [
      publication?.name,
      publication?.description,
      publication?.descriptionDetail,
      itemImages,
      meal,
      withMeal,
    ]
  )
}

/**
 * Plan card for the facility-wide plan index (`/:property/plans`).
 *
 * Based on `PlanItem` but the CTA navigates to the plan-first room list
 * (`/:property/:plan/rooms`, PR #1907) instead of the booking/options flow:
 * here the guest picks a plan first, then a room. `PlanItem` (the room-scoped
 * booking card) is intentionally left untouched.
 *
 * `price` is the representative "from" price (cheapest reservable listing),
 * computed server-side by `getHotelPlans`.
 */
export const HotelPlanItem: React.FC<HotelPlanItemProps> = ({
  meal: mealType,
  publications,
  images,
  price = 0,
  cancelPolicy,
  slug = '',
  canReserve = false,
  checkInTimeStart: checkInTimeStartProp = '15:00',
  checkInTimeEnd: checkInTimeEndProp = '24:00',
  checkOutTimeEnd: checkOutTimeEndProp = '10:00',
  checkInTimeStartNote,
  checkInTimeEndNote,
  checkOutTimeEndNote,
  collectAccommodationTaxOnSite,
  collectBathTaxOnSite,
}) => {
  const { t, i18n } = useTranslation()
  const [isModalOpen, setModalOpen] = useState(false)
  const [isOpenDetailModal, toggleOpenDetailModal] = useState(false)
  const { params } = useSearchQueryParams()
  const { isHotel, isActivity } = usePropertyProvider()

  const {
    planName,
    planDescription,
    planDescriptionDetail,
    meal,
    withMeal,
    itemImages,
  } = useDefaultValues({
    publications,
    images,
    mealType,
  })
  const publication = usePublication(i18n.language, publications)
  const { onCreateNextPath } = useCreateNextPath()
  const navigate = useNavigate()
  const [isOpenGalleryModal, toggleGalleryModal] = useState(false)
  const [galleryInitialSlide, setGalleryInitialSlide] = useState(0)
  // price 0 means "stop selling", so the plan is not selectable.
  const isAvailable = !!slug && !!canReserve && price > 0

  const { numberOfAdults, numberOfChildren, numberOfInfants, roomCount } =
    params

  // Plan-first flow: pick this plan, then choose a room on /:property/:plan/rooms.
  // Strip any stale `?room=` filter: this plan list was fetched without a room
  // scope, but PlanRoomIndexFrame reads `params.room` as `roomSlug` and would
  // otherwise narrow (or empty) the room picker to a single room (Codex P2).
  // `room: ''` deletes the key in updateSearchParams; dates/guests are kept.
  const nextPath = onCreateNextPath(PATH.plan.rooms, {
    queryParams: { ...params, room: '' },
    pathParams: { plan: slug },
  })

  const checkInTimeStart =
    checkInTimeStartNote || checkInTimeStartProp || '15:00'
  const checkInTimeEnd = checkInTimeEndNote || checkInTimeEndProp || '24:00'
  const checkOutTimeEnd = checkOutTimeEndNote || checkOutTimeEndProp || '10:00'

  return (
    <Fragment>
      {isOpenDetailModal && (
        <PlanDetailModal
          isOpen={isOpenDetailModal}
          images={images}
          onClose={() => toggleOpenDetailModal(false)}
          planName={publication?.name ?? ''}
          description={publication?.description ?? ''}
          content={publication?.descriptionDetail ?? ''}
          withMeal={withMeal}
          meal={meal}
          checkIn={`${checkInTimeStart ?? '15:00'} ~ ${
            checkInTimeEnd ?? '24:00'
          }`}
          checkOut={checkOutTimeEnd ?? '10:00'}
          isHotel={isHotel ?? false}
        />
      )}
      {isOpenGalleryModal && (
        <GalleryModal
          isOpen={isOpenGalleryModal}
          onClose={() => toggleGalleryModal(false)}
          items={itemImages}
          initialSlide={galleryInitialSlide}
          renderItem={(item) => <img src={item.pc.src} alt={item.alt} />}
        />
      )}
      <Container>
        <Row gutterWidth={20} gutterHeight={10}>
          <Col pc={6}>
            <SlideShow
              items={itemImages}
              renderItem={(item) => (
                <figure>
                  <img
                    src={item.pc.src}
                    alt={item.alt}
                    className="pc"
                    width="600"
                    height="400"
                  />
                  <img
                    src={item.sp.src}
                    alt={item.alt}
                    className="sp"
                    width="600"
                    height="400"
                  />
                </figure>
              )}
              onTap={(swiper) => {
                setGalleryInitialSlide(swiper.activeIndex)
                toggleGalleryModal(true)
              }}
            />
          </Col>
          <Col pc={6}>
            <div className={styles.item}>
              <div className={styles.itemName}>
                <h6>{planName}</h6>
              </div>
              <div className={styles.itemInfo}>
                {isHotel ? (
                  <React.Fragment>
                    <Icon mark="time">
                      {t(`${translateNamespace}.checkIn`)}: {checkInTimeStart} ~{' '}
                      {checkInTimeEnd}
                    </Icon>
                    <Icon mark="time">
                      {t(`${translateNamespace}.checkOut`)}: ~ {checkOutTimeEnd}
                    </Icon>
                    <Icon mark="meal">
                      {withMeal} {withMeal && meal && <span> （{meal}）</span>}
                    </Icon>
                  </React.Fragment>
                ) : (
                  !isActivity && (
                    <Icon mark="time">
                      {t(`${translateNamespace}.availableTime`)}：
                      {checkInTimeStart} ~ {checkOutTimeEnd}
                    </Icon>
                  )
                )}
                <Icon mark="close">
                  <Link
                    to="#"
                    className={styles.link}
                    onClick={(e) => {
                      e.preventDefault()
                      setModalOpen(true)
                    }}
                  >
                    {t(`${translateNamespace}.cancel`)}
                  </Link>
                  <Modal
                    isOpen={isModalOpen}
                    onClose={() => setModalOpen(false)}
                    title={t('components.CancelPolicy.cancelTitle')}
                  >
                    <CancelPolicy {...cancelPolicy} planName={planName} />
                  </Modal>
                </Icon>
              </div>
              {isAvailable && price > 0 && (
                <div className={styles.itemPrice}>
                  <div className={styles.roomsAndGuests}>
                    <BaseText color="gray3" size={12}>
                      {t(`${translateNamespace}.guests`, {
                        numberOfAdults,
                        numberOfChildren,
                        numberOfInfants,
                      })}
                      {isHotel &&
                        `・${t(`${translateNamespace}.rooms`, {
                          rooms: roomCount,
                        })}`}
                    </BaseText>
                  </div>
                  {/* "〜円から" — representative (cheapest listing) price. */}
                  <div className={styles.price}>
                    <b className={styles.num}>{price.toLocaleString()}</b>
                    {t(`${hotelPlanNamespace}.priceFrom`)}
                  </div>
                  <TaxLabel
                    collectAccommodationTaxOnSite={
                      collectAccommodationTaxOnSite
                    }
                    collectBathTaxOnSite={collectBathTaxOnSite}
                  />
                </div>
              )}
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className={styles.desc}>
              <p>{planDescription}</p>
              {(isHotel || planDescriptionDetail) && (
                <Button
                  htmlType="button"
                  outline
                  size="smallWide"
                  variant="secondary"
                  position="center"
                  onClick={() => toggleOpenDetailModal(true)}
                >
                  {t(`${translateNamespace}.detail`)}
                </Button>
              )}
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className={styles.button}>
              <Button
                htmlType="button"
                variant="primary"
                size="full"
                position="right"
                disabled={!isAvailable}
                onClick={() => {
                  // ButtonWrapper's `disabled` is CSS-only (no native disabled
                  // attribute), so a focused button can still fire onClick via
                  // Enter/Space. Guard here so an unavailable plan never
                  // navigates into the room flow (Codex P2).
                  if (!isAvailable) {
                    return
                  }
                  navigate(nextPath)
                }}
              >
                {isAvailable
                  ? t(`${hotelPlanNamespace}.selectPlan`)
                  : t(`${translateNamespace}.notAvailable`)}
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </Fragment>
  )
}
